import React, { useState, useEffect } from 'react';
import { X, Loader, Trash2, Calendar, Archive } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { logModalDecision } from '../lib/modalLog';
import { type ReportRecommendation } from '../lib/reportingSupabase';
import toast from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface EditProjectModalProps {
  project: any;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditProjectModal: React.FC<EditProjectModalProps> = ({ project, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [linkedReports, setLinkedReports] = useState<{id: string, name: string, share_url: string}[]>([]);
  const [reportRecommendations] = useState<ReportRecommendation[]>([]);
  const [linkedReleases, setLinkedReleases] = useState<any[]>([]);
  const [releaseSearch, setReleaseSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [reportSearch, setReportSearch] = useState('');
  const [reportSearchResults, setReportSearchResults] = useState<any[]>([]);
  const [shiftSearch, setShiftSearch] = useState('');
  const [shiftSearchResults, setShiftSearchResults] = useState<any[]>([]);
  const [shiftTarget, setShiftTarget] = useState<any>(null);
  const [shifting, setShifting] = useState(false);
  const [budgetShiftLogs, setBudgetShiftLogs] = useState<any[]>([]);
  const [currentSpend, setCurrentSpend] = useState<number | null>(null);
  const [relatedCounts, setRelatedCounts] = useState({
    releases: 0,
    submissions: 0,
    adCreatives: 0
  });
  const [formData, setFormData] = useState({
    project_name: project.project_name || '',
    project_status: project.project_status || 'onboarding',
    service_package: project.service_package || 'full',
    artist_type: (project as any).artist_type || '',
    artist_language: (project as any).artist_language || '',
    
    // Onboarding
    meta_access_granted: project.meta_access_granted || false,
    masterclass_login_created: project.masterclass_login_created || false,
    masterclass_watched: project.masterclass_watched || false,
    release_sheet_completed: project.release_sheet_completed || false,
    first_four_videos_uploaded: project.first_four_videos_uploaded || false,
    
    // Content Production
    content_call_scheduled: project.content_call_scheduled || false,
    content_call_prepared: project.content_call_prepared || false,
    content_call_completed: project.content_call_completed || false,
    
    // Timeline
    posting_start_date: project.posting_start_date || '',
    release_date: project.release_date || '',
    campaign_start_date: project.campaign_start_date || project.release_date || '',
    campaign_end_date: project.campaign_end_date || (project.release_date ? (() => { const d = new Date(project.release_date); d.setDate(d.getDate() + 30); return d.toISOString().split('T')[0]; })() : ''),
    
    // Follow-up
    feedback_call_scheduled: project.feedback_call_scheduled || false,
    feedback_call_completed: project.feedback_call_completed || false,
    google_review_requested: project.google_review_requested || false,
    google_review_received: project.google_review_received || false,
    
    // Budget
    ad_budget: project.ad_budget ? project.ad_budget / 100 : '',
    planned_budget_meta: project.planned_budget_meta ? project.planned_budget_meta / 100 : '',
    planned_budget_tiktok: project.planned_budget_tiktok ? project.planned_budget_tiktok / 100 : '',
    planned_budget_youtube: project.planned_budget_youtube ? project.planned_budget_youtube / 100 : '',
    planned_budget_follower: project.planned_budget_follower ? project.planned_budget_follower / 100 : '',
    planned_budget_additional: project.planned_budget_additional ? project.planned_budget_additional / 100 : '',
    
    // Links
    reporting_dashboard_url: project.reporting_dashboard_url || '',
    notion_page_url: project.notion_page_url || '',
    song_so_urls: project.song_so_urls?.join('\n') || '',
    
    notes: project.notes || ''
  });

  // Fetch linked releases
  useEffect(() => {
    const loadLinkedReleases = async () => {
      // Check project_releases junction table
      const { data: junctionData } = await supabase
        .from('project_releases')
        .select(`
          id,
          campaign_name,
          release:releases(id, name, artist:artists(name))
        `)
        .eq('project_id', project.id);

      // Also check if project has a direct release_id link (old system)
      let directRelease = null;
      if (project.release_id) {
        const { data: releaseData } = await supabase
          .from('releases')
          .select('id, name, artist:artists(name)')
          .eq('id', project.release_id)
          .single();
        
        if (releaseData) {
          directRelease = {
            id: `direct-${project.release_id}`, // Unique ID for direct link
            campaign_name: null,
            release: releaseData,
            isDirect: true // Flag to identify direct links
          };
        }
      }

      // Combine both sources
      const allReleases: any[] = [
        ...(junctionData || []),
        ...(directRelease ? [directRelease] : [])
      ];

      if (allReleases.length > 0) {
        // Deduplicate by release id to prevent duplicate key warnings
        const unique = Array.from(new Map(allReleases.map(item => [item.release?.id || item.id, item])).values());
        setLinkedReleases(unique);
      }
    };

    loadLinkedReleases();
  }, [project.id, project.release_id]);

  // Fetch related data counts when delete confirmation is shown
  useEffect(() => {
    if (showDeleteConfirm || showArchiveConfirm) {
      const fetchRelatedCounts = async () => {
        try {
          // Get all linked release IDs
          const { data: linkedReleaseData } = await supabase
            .from('project_releases')
            .select('release_id')
            .eq('project_id', project.id);

          const releaseIds: any[] = linkedReleaseData?.map((r: any) => r.release_id) || [];
          if (project.release_id && !releaseIds.includes(project.release_id)) {
            releaseIds.push(project.release_id);
          }

          let submissionsCount = 0;
          let adCreativesCount = 0;

          if (releaseIds.length > 0) {
            // Count video submissions linked to these releases
            const { count: sCount } = await supabase
              .from('submissions')
              .select('*', { count: 'exact', head: true })
              .in('release_id', releaseIds);
            submissionsCount = sCount || 0;

            // Count ad creatives linked to these releases
            const { count: aCount } = await supabase
              .from('ad_creatives')
              .select('*', { count: 'exact', head: true })
              .in('release_id', releaseIds);
            adCreativesCount = aCount || 0;
          }

          setRelatedCounts({
            releases: releaseIds.length,
            submissions: submissionsCount,
            adCreatives: adCreativesCount
          });
        } catch (error) {
          console.error('Error fetching related counts:', error);
        }
      };

      fetchRelatedCounts();
    }
  }, [showDeleteConfirm, showArchiveConfirm, project.id, project.release_name, project.artist_id]);

  // Load budget shift logs and current spend
  useEffect(() => {
    const loadBudgetData = async () => {
      // Load shift logs (both sent and received)
      const { data: logs } = await supabase
        .from('budget_shift_log')
        .select('*')
        .or(`from_project_id.eq.${project.id},to_project_id.eq.${project.id}`)
        .order('created_at', { ascending: false }) as any;
      
      if (logs) {
        // Enrich with project names
        const projectIds = [...new Set(logs.flatMap((l: any) => [l.from_project_id, l.to_project_id]))];
        const { data: projectNames } = await supabase
          .from('dashboard_projects')
          .select('id, release_name, artist_name')
          .in('id', projectIds) as any;
        
        const nameMap: Record<string, string> = {};
        (projectNames || []).forEach((p: any) => { nameMap[p.id] = `${p.artist_name} - ${p.release_name}`; });
        
        setBudgetShiftLogs(logs.map((l: any) => ({ ...l, fromName: nameMap[l.from_project_id] || 'Unknown', toName: nameMap[l.to_project_id] || 'Unknown' })));
      }

      // Load current spend from cache
      const { data: cache } = await supabase
        .from('campaign_metrics_cache')
        .select('spend')
        .eq('project_id', project.id)
        .maybeSingle() as any;
      
      if (cache) {
        setCurrentSpend(parseFloat(cache.spend || '0'));
      }
    };

    loadBudgetData();
  }, [project.id]);

  // Search projects for budget shift
  const searchShiftTargets = async (query: string) => {
    if (!query || query.length < 2) {
      setShiftSearchResults([]);
      return;
    }
    const { data } = await supabase
      .from('dashboard_projects')
      .select('id, release_name, artist_name, ad_budget')
      .neq('id', project.id)
      .or(`release_name.ilike.%${query}%,artist_name.ilike.%${query}%`) as any;
    
    setShiftSearchResults(data || []);
  };

  // Execute budget shift
  const executeBudgetShift = async () => {
    console.log(`%c[EditModal] executeBudgetShift START`, 'color: #f59e0b; font-weight: bold', {
      project_id: project.id,
      artist: project.artist_name,
      release: project.release_name,
      shiftTarget: shiftTarget ? { id: shiftTarget.id, artist_name: shiftTarget.artist_name, release_name: shiftTarget.release_name } : null,
      currentSpend,
    });
    if (!shiftTarget || currentSpend === null) {
      console.warn(`%c[EditModal] executeBudgetShift → SKIP: missing shiftTarget or currentSpend`, 'color: #f59e0b; font-weight: bold', { shiftTarget, currentSpend });
      return;
    }
    setShifting(true);

    try {
      const currentBudgetCents = project.ad_budget || 0;
      const spendCents = Math.round(currentSpend * 100);
      const shiftAmountCents = currentBudgetCents - spendCents;
      console.log(`%c[EditModal] executeBudgetShift calculation`, 'color: #f59e0b; font-weight: bold', {
        currentBudgetCents, spendCents, shiftAmountCents, shiftAmountEuros: shiftAmountCents / 100,
      });

      if (shiftAmountCents <= 0) {
        toast.error('Kein ungenutztes Budget zum Verschieben');
        setShifting(false);
        return;
      }

      const targetCurrentBudget = shiftTarget.ad_budget || 0;
      const targetNewBudget = targetCurrentBudget + shiftAmountCents;

      // Calculate dates
      const today = new Date().toISOString().split('T')[0];
      const shiftAmountEuro = shiftAmountCents / 100;
      const extraDays = Math.ceil(shiftAmountEuro / 20);
      const targetEndDate = new Date();
      targetEndDate.setDate(targetEndDate.getDate() + extraDays);
      const targetEndDateStr = targetEndDate.toISOString().split('T')[0];

      // Update source project: budget to spend, end date to today
      await supabase
        .from('project_tracking')
        .update({ ad_budget: spendCents, campaign_end_date: today } as any)
        .eq('id', project.id);

      // Update target project: budget + shift, end date extended
      await supabase
        .from('project_tracking')
        .update({ ad_budget: targetNewBudget, campaign_end_date: targetEndDateStr } as any)
        .eq('id', shiftTarget.id);

      // Log the shift
      await supabase
        .from('budget_shift_log')
        .insert({
          from_project_id: project.id,
          to_project_id: shiftTarget.id,
          amount_cents: shiftAmountCents,
          from_original_budget_cents: currentBudgetCents,
          from_new_budget_cents: spendCents,
          to_original_budget_cents: targetCurrentBudget,
          to_new_budget_cents: targetNewBudget,
        } as any);

      // Log the modal decision
      await logModalDecision(project.id, 'budget_shift', 'shifted_budget', {
        from_project_id: project.id,
        to_project_id: shiftTarget.id,
        to_project_name: `${shiftTarget.artist_name} - ${shiftTarget.release_name}`,
        amount_cents: shiftAmountCents,
        amount_euros: shiftAmountCents / 100,
        from_original_budget_cents: currentBudgetCents,
        from_new_budget_cents: spendCents,
        to_original_budget_cents: targetCurrentBudget,
        to_new_budget_cents: targetNewBudget,
        current_spend: currentSpend,
        extra_days: extraDays,
      });

      // Update local form
      setFormData(prev => ({ ...prev, ad_budget: (spendCents / 100).toString() as any }));
      setShiftTarget(null);
      setShiftSearch('');

      toast.success(`${(shiftAmountCents / 100).toFixed(2)}€ verschoben zu ${shiftTarget.artist_name} - ${shiftTarget.release_name}`);
      onSuccess();
    } catch (error) {
      console.error('Error shifting budget:', error);
      toast.error('Fehler beim Verschieben des Budgets');
    } finally {
      setShifting(false);
    }
  };

  // Load linked reports from DB on mount
  useEffect(() => {
    const loadLinkedReports = async () => {
      const reportIds: string[] = project.linked_report_ids || [];
      if (reportIds.length === 0) return;

      // Fetch report details from reporting DB
      try {
        const response = await fetch(`https://uydhsjvwrgupgfjevqsz.supabase.co/rest/v1/reports?id=in.(${reportIds.join(',')})&select=id,name,share_url`, {
          headers: {
            'apikey': 'sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5',
            'Authorization': 'Bearer sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setLinkedReports(data || []);
        }
      } catch (error) {
        console.error('Error loading linked reports:', error);
      }
    };

    loadLinkedReports();
  }, [project.linked_report_ids]);

  // Search reports in reporting database
  const searchReports = async (query: string) => {
    if (!query || query.length < 2) {
      setReportSearchResults([]);
      return;
    }

    try {
      // Search for reports by campaign name using direct table query
      const response = await fetch(`https://uydhsjvwrgupgfjevqsz.supabase.co/rest/v1/reports?name=ilike.*${encodeURIComponent(query)}*&select=id,name,share_url&limit=10`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': 'sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5',
          'Authorization': 'Bearer sb_publishable_BrCqK-o8PBNyoNFYpxWoxg_qDwheRR5',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReportSearchResults(data || []);
      } else {
        console.error('Report search failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error searching reports:', error);
    }
  };

  // Link report to project (add to array)
  const linkReport = async (report: {id: string, name: string, share_url: string}) => {
    console.log(`%c[EditModal] linkReport START`, 'color: #10b981; font-weight: bold', {
      project_id: project.id, report, currentLinked: linkedReports.map(r => r.id),
    });
    // Don't add duplicates
    if (linkedReports.some(r => r.id === report.id)) {
      console.warn(`%c[EditModal] linkReport → SKIP: duplicate`, 'color: #f59e0b; font-weight: bold', { reportId: report.id });
      toast.error('Report bereits verlinkt');
      return;
    }

    const newReports = [...linkedReports, report];
    setLinkedReports(newReports);
    setReportSearch('');
    setReportSearchResults([]);

    // Save to DB immediately
    const newIds = newReports.map(r => r.id);
    console.log(`%c[EditModal] linkReport → DB update`, 'color: #10b981; font-weight: bold', { newIds });
    await supabase
      .from('project_tracking')
      .update({ linked_report_ids: newIds } as any)
      .eq('id', project.id);

    // Clear metrics cache so fresh data is fetched
    await supabase
      .from('campaign_metrics_cache')
      .delete()
      .eq('project_id', project.id);

    // Log the decision
    await logModalDecision(project.id, 'report_link', 'linked_report', {
      report_id: report.id,
      report_name: report.name,
      linked_report_ids: newIds,
    });

    console.log(`%c[EditModal] linkReport ✅ DONE`, 'color: #10b981; font-weight: bold');
    toast.success('Report verlinkt');
  };

  // Unlink a specific report from project
  const unlinkReport = async (reportId: string) => {
    const removedReport = linkedReports.find(r => r.id === reportId);
    console.log(`%c[EditModal] unlinkReport START`, 'color: #ef4444; font-weight: bold', {
      project_id: project.id, reportId, reportName: removedReport?.name, currentLinked: linkedReports.map(r => r.id),
    });
    const newReports = linkedReports.filter(r => r.id !== reportId);
    setLinkedReports(newReports);

    // Save to DB immediately
    const newIds = newReports.map(r => r.id);
    console.log(`%c[EditModal] unlinkReport → DB update`, 'color: #ef4444; font-weight: bold', { removedReportId: reportId, newIds });
    await supabase
      .from('project_tracking')
      .update({ linked_report_ids: newIds } as any)
      .eq('id', project.id);

    // Clear metrics cache
    await supabase
      .from('campaign_metrics_cache')
      .delete()
      .eq('project_id', project.id);

    // Log the decision
    await logModalDecision(project.id, 'report_unlink', 'unlinked_report', {
      report_id: reportId,
      report_name: removedReport?.name || null,
      linked_report_ids: newIds,
    });

    console.log(`%c[EditModal] unlinkReport ✅ DONE`, 'color: #ef4444; font-weight: bold');
    toast.success('Report entfernt');
  };

  // Search releases
  const searchReleases = async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    // Search by release name
    const { data: releaseData } = await supabase
      .from('releases')
      .select('id, name, artist:artists(name)')
      .ilike('name', `%${query}%`)
      .limit(10);

    // Search by artist name
    const { data: artistData } = await supabase
      .from('releases')
      .select('id, name, artist:artists!inner(name)')
      .ilike('artists.name', `%${query}%`)
      .limit(10);

    // Combine and deduplicate results
    const combined = [...(releaseData || []), ...(artistData || [])];
    const unique = Array.from(new Map(combined.map(item => [item.id, item])).values());
    
    // Filter out already linked releases
    const linkedIds = new Set(linkedReleases.map((r: any) => r.release?.id));
    const filtered = unique.filter((r: any) => !linkedIds.has(r.id));
    
    setSearchResults(filtered.slice(0, 10));
  };

  // Link release to project
  const linkRelease = async (releaseId: string, releaseName: string, artistName: string) => {
    try {
      // Check if this release is already linked to another project
      const { data: existingProject, error: checkError } = await supabase
        .from('project_tracking')
        .select('id')
        .eq('release_id', releaseId as any)
        .maybeSingle();

      if (checkError) {
        toast.error('Error checking for existing project');
        return;
      }

      // If release is already in another project, merge them
      if (existingProject && (existingProject as any).id !== project.id) {
        const shouldMerge = confirm(
          `This release is already linked to another project. Do you want to merge that project into this one and delete the other project?`
        );

        if (!shouldMerge) {
          return;
        }

        // Link the release to this project
        await supabase
          .from('project_releases')
          .insert({
            project_id: project.id,
            release_id: releaseId,
            campaign_name: artistName || releaseName
          } as any);

        // Delete the other project (cascade will handle related data)
        const { error: deleteError } = await supabase
          .from('project_tracking')
          .delete()
          .eq('id', (existingProject as any).id);

        if (deleteError) {
          toast.error('Error merging projects');
          return;
        }

        toast.success('Projects merged successfully');
      } else {
        // Normal link - no existing project
        const { error } = await supabase
          .from('project_releases')
          .insert({
            project_id: project.id,
            release_id: releaseId,
            campaign_name: artistName || releaseName
          } as any);

        if (error) {
          if (error.code === '23505') {
            toast.error('Release ist bereits verlinkt');
          } else {
            toast.error('Error linking release');
          }
          return;
        }

        toast.success('Release linked to project');
      }
      
      setReleaseSearch('');
      setSearchResults([]);
      
      // Reload linked releases
      const { data } = await supabase
        .from('project_releases')
        .select(`
          id,
          campaign_name,
          release:releases(id, name, artist:artists(name))
        `)
        .eq('project_id', project.id);
      
      if (data) {
        // Deduplicate by id to prevent duplicate key warnings
        const unique = Array.from(new Map((data as any[]).map(item => [item.id, item])).values());
        setLinkedReleases(unique);
      }
    } catch (error) {
      console.error('Error linking release:', error);
      toast.error('Failed to link release');
    }
  };

  // Unlink release from project
  const unlinkRelease = async (linkId: string) => {
    const { error } = await supabase
      .from('project_releases')
      .delete()
      .eq('id', linkId as any);

    if (!error) {
      toast.success('Release unlinked');
      setLinkedReleases(linkedReleases.filter(r => r.id !== linkId));
    } else {
      toast.error('Failed to unlink release');
    }
  };

  const handleArchive = async () => {
    setArchiving(true);

    try {
      // Get all linked release IDs
      const { data: linkedReleaseData } = await supabase
        .from('project_releases')
        .select('release_id')
        .eq('project_id', project.id);

      const releaseIds: any[] = linkedReleaseData?.map((r: any) => r.release_id) || [];

      // Also include direct release_id
      if (project.release_id && !releaseIds.includes(project.release_id)) {
        releaseIds.push(project.release_id);
      }

      // 1. Archive video submissions linked to these releases
      if (releaseIds.length > 0) {
        await supabase
          .from('submissions')
          .update({ status: 'archived' } as any)
          .in('release_id', releaseIds);
      }

      // 2. Archive ad creatives linked to these releases
      if (releaseIds.length > 0) {
        await supabase
          .from('ad_creatives')
          .update({ status: 'archived' } as any)
          .in('release_id', releaseIds);
      }

      // 3. Archive the releases themselves
      if (releaseIds.length > 0) {
        await supabase
          .from('releases')
          .update({ status: 'archived' } as any)
          .in('id', releaseIds);
      }

      // 4. Archive the project
      const { error } = await supabase
        .from('project_tracking')
        .update({ project_status: 'archived' } as any)
        .eq('id', project.id);

      if (error) throw error;

      toast.success('Projekt und alle zugehörigen Daten archiviert');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error archiving project:', error);
      toast.error('Fehler beim Archivieren des Projekts');
    } finally {
      setArchiving(false);
      setShowArchiveConfirm(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);

    try {
      // Get all linked release IDs
      const { data: linkedReleaseData } = await supabase
        .from('project_releases')
        .select('release_id')
        .eq('project_id', project.id);

      const releaseIds: any[] = linkedReleaseData?.map((r: any) => r.release_id) || [];

      // Also include direct release_id
      if (project.release_id && !releaseIds.includes(project.release_id)) {
        releaseIds.push(project.release_id);
      }

      // 1. Delete video submissions linked to these releases
      if (releaseIds.length > 0) {
        await supabase
          .from('submissions')
          .delete()
          .in('release_id', releaseIds);
      }
      // Also delete by project_name (legacy)
      if (project.release_name) {
        await supabase
          .from('submissions')
          .delete()
          .eq('project_name', project.release_name as any);
      }

      // 2. Delete ad creatives linked to these releases
      if (releaseIds.length > 0) {
        await supabase
          .from('ad_creatives')
          .delete()
          .in('release_id', releaseIds);
      }

      // 3. Delete the releases themselves
      if (releaseIds.length > 0) {
        await supabase
          .from('releases')
          .delete()
          .in('id', releaseIds);
      }

      // 4. Delete project_releases links
      await supabase
        .from('project_releases')
        .delete()
        .eq('project_id', project.id);

      // 5. Finally, delete the project itself
      const { error } = await supabase
        .from('project_tracking')
        .delete()
        .eq('id', project.id);

      if (error) throw error;

      toast.success('Projekt und alle zugehörigen Daten gelöscht');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Fehler beim Löschen des Projekts');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData: any = {
        project_name: formData.project_name || null,
        project_status: formData.project_status,
        
        // Onboarding
        meta_access_granted: formData.meta_access_granted,
        meta_access_granted_date: formData.meta_access_granted && !project.meta_access_granted_date ? new Date().toISOString() : project.meta_access_granted_date,
        masterclass_login_created: formData.masterclass_login_created,
        masterclass_login_created_date: formData.masterclass_login_created && !project.masterclass_login_created_date ? new Date().toISOString() : project.masterclass_login_created_date,
        masterclass_watched: formData.masterclass_watched,
        masterclass_watched_date: formData.masterclass_watched && !project.masterclass_watched_date ? new Date().toISOString() : project.masterclass_watched_date,
        release_sheet_completed: formData.release_sheet_completed || !!formData.notion_page_url,
        release_sheet_completed_date: (formData.release_sheet_completed || !!formData.notion_page_url) && !project.release_sheet_completed_date ? new Date().toISOString() : project.release_sheet_completed_date,
        first_four_videos_uploaded: formData.first_four_videos_uploaded,
        first_four_videos_uploaded_date: formData.first_four_videos_uploaded && !project.first_four_videos_uploaded_date ? new Date().toISOString() : project.first_four_videos_uploaded_date,
        
        // Content Production
        content_call_scheduled: formData.content_call_scheduled,
        content_call_scheduled_date: formData.content_call_scheduled && !project.content_call_scheduled_date ? new Date().toISOString() : project.content_call_scheduled_date,
        content_call_prepared: formData.content_call_prepared,
        content_call_prepared_date: formData.content_call_prepared && !project.content_call_prepared_date ? new Date().toISOString() : project.content_call_prepared_date,
        content_call_completed: formData.content_call_completed,
        content_call_completed_date: formData.content_call_completed && !project.content_call_completed_date ? new Date().toISOString() : project.content_call_completed_date,
        
        // Timeline
        posting_start_date: formData.posting_start_date || null,
        release_date: formData.release_date || null,
        campaign_start_date: formData.campaign_start_date || null,
        campaign_end_date: formData.campaign_end_date || null,
        
        // Follow-up
        feedback_call_scheduled: formData.feedback_call_scheduled,
        feedback_call_scheduled_date: formData.feedback_call_scheduled && !project.feedback_call_scheduled_date ? new Date().toISOString() : project.feedback_call_scheduled_date,
        feedback_call_completed: formData.feedback_call_completed,
        feedback_call_completed_date: formData.feedback_call_completed && !project.feedback_call_completed_date ? new Date().toISOString() : project.feedback_call_completed_date,
        google_review_requested: formData.google_review_requested,
        google_review_requested_date: formData.google_review_requested && !project.google_review_requested_date ? new Date().toISOString() : project.google_review_requested_date,
        google_review_received: formData.google_review_received,
        google_review_received_date: formData.google_review_received && !project.google_review_received_date ? new Date().toISOString() : project.google_review_received_date,
        
        // Budget
        ad_budget: formData.ad_budget ? Math.round(Number(formData.ad_budget) * 100) : 0,
        planned_budget_meta: formData.planned_budget_meta ? Math.round(Number(formData.planned_budget_meta) * 100) : 0,
        planned_budget_tiktok: formData.planned_budget_tiktok ? Math.round(Number(formData.planned_budget_tiktok) * 100) : 0,
        planned_budget_youtube: formData.planned_budget_youtube ? Math.round(Number(formData.planned_budget_youtube) * 100) : 0,
        planned_budget_follower: formData.planned_budget_follower ? Math.round(Number(formData.planned_budget_follower) * 100) : 0,
        planned_budget_additional: formData.planned_budget_additional ? Math.round(Number(formData.planned_budget_additional) * 100) : 0,
        // Acknowledge the budget so the overspend modal doesn't reappear after saving
        budget_ack_budget: formData.ad_budget ? Number(formData.ad_budget) : 0,
        
        // Links
        reporting_dashboard_url: formData.reporting_dashboard_url || null,
        notion_page_url: formData.notion_page_url || null,
        song_so_urls: formData.song_so_urls ? formData.song_so_urls.split('\n').filter((url: string) => url.trim()) : null,
        
        notes: formData.notes || null
      };

      const { error } = await supabase
        .from('project_tracking')
        .update(updateData)
        .eq('id', project.id);

      if (error) throw error;

      // Update service_package on the release
      if (project.release_id && formData.service_package !== project.service_package) {
        await supabase
          .from('releases')
          .update({ service_package: formData.service_package } as any)
          .eq('id' as any, project.release_id as any);
      }

      // Update artist_type and artist_language on the artist
      const artistUpdates: any = {};
      if (formData.artist_type !== (project as any).artist_type) {
        artistUpdates.artist_type = formData.artist_type || null;
      }
      if (formData.artist_language !== (project as any).artist_language) {
        artistUpdates.artist_language = formData.artist_language || null;
      }
      if (Object.keys(artistUpdates).length > 0 && project.artist_id) {
        await supabase
          .from('artists')
          .update(artistUpdates)
          .eq('id', project.artist_id);
      }

      // Log the budget edit if the budget changed
      const oldBudgetEuros = project.ad_budget ? project.ad_budget / 100 : 0;
      const newBudgetEuros = formData.ad_budget ? Number(formData.ad_budget) : 0;
      const budgetChanged = Math.abs(oldBudgetEuros - newBudgetEuros) > 0.01;
      console.log(`%c[EditModal] handleSubmit budget check`, 'color: #8b5cf6; font-weight: bold', {
        project_id: project.id,
        oldBudgetEuros,
        newBudgetEuros,
        budgetChanged,
        diff: Math.abs(oldBudgetEuros - newBudgetEuros),
      });
      if (budgetChanged) {
        console.log(`%c[EditModal] handleSubmit → logging budget change to modal_decision_log`, 'color: #8b5cf6; font-weight: bold');
        await logModalDecision(project.id, 'budget_edit_save', 'saved_new_budget', {
          old_budget: oldBudgetEuros,
          new_budget: newBudgetEuros,
          source: 'edit_modal',
        });
      }

      console.log(`%c[EditModal] handleSubmit ✅ DONE`, 'color: #22c55e; font-weight: bold', { project_id: project.id });
      toast.success('Projekt erfolgreich aktualisiert');
      onSuccess();
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Fehler beim Aktualisieren des Projekts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-6 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-white">Projekt bearbeiten</h2>
            <input
              type="text"
              value={formData.project_name}
              onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
              placeholder={(() => {
                const releaseName = project.release_name;
                // If the name contains }}, it's likely followed by JSON content
                if (releaseName && releaseName.includes('}}')) {
                  const titlePart = releaseName.split('}}')[0];
                  return `${project.artist_name} - ${titlePart || releaseName}`;
                }
                return `${project.artist_name} - ${releaseName}`;
              })()}
              className="text-gray-400 text-sm mt-1 bg-transparent border-none outline-none focus:text-white w-full cursor-text hover:text-gray-300 transition-colors"
            />
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Project Status & Package */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Projekt Status
              </label>
              <select
                value={formData.project_status}
                onChange={(e) => setFormData({ ...formData, project_status: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="onboarding">Onboarding</option>
                <option value="content_production">Content Production</option>
                <option value="active_campaign">Active Campaign</option>
                <option value="completed">Completed</option>
                <option value="paused">Paused</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Paket
              </label>
              <select
                value={formData.service_package}
                onChange={(e) => setFormData({ ...formData, service_package: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="full">Full</option>
                <option value="content_only">Content Only</option>
                <option value="ad_only">Ads Only</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Artist-Typ
              </label>
              <select
                value={formData.artist_type}
                onChange={(e) => setFormData({ ...formData, artist_type: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">—</option>
                <option value="solo">Solo Artist</option>
                <option value="band">Band</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Sprache
              </label>
              <select
                value={formData.artist_language}
                onChange={(e) => setFormData({ ...formData, artist_language: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">—</option>
                <option value="de">Deutsch</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>

          {/* Linked Releases */}
          <div className="border border-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Linked Releases</h3>
            
            {/* Search Bar */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search releases by name or artist..."
                value={releaseSearch}
                onChange={(e) => {
                  setReleaseSearch(e.target.value);
                  searchReleases(e.target.value);
                }}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {searchResults.length > 0 && (
                <div className="mt-2 bg-gray-800 border border-gray-700 rounded-lg max-h-48 overflow-y-auto">
                  {searchResults.map((release: any) => (
                    <div
                      key={release.id}
                      onClick={() => linkRelease(release.id, release.name, release.artist?.name)}
                      className="px-3 py-2 hover:bg-gray-700 cursor-pointer text-sm text-white"
                    >
                      <div className="font-medium">{release.name}</div>
                      <div className="text-xs text-gray-400">{release.artist?.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Linked Releases List */}
            {linkedReleases.length > 0 ? (
              <div className="space-y-2">
                {linkedReleases.map((link: any) => (
                  <div key={link.id} className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                    <div>
                      <div className="text-sm font-medium text-white">
                        {(() => {
                          const releaseName = link.release?.name;
                          // If the name contains }}, it's likely followed by JSON content
                          if (releaseName && releaseName.includes('}}')) {
                            const titlePart = releaseName.split('}}')[0];
                            return titlePart || releaseName;
                          }
                          return releaseName;
                        })()}
                      </div>
                      <div className="text-xs text-gray-400">{link.release?.artist?.name}</div>
                      {link.campaign_name && (
                        <div className="text-xs text-green-400 mt-1">📊 Campaign: {link.campaign_name}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => unlinkRelease(link.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No releases linked yet. Search above to add releases.</p>
            )}
          </div>

          {/* Onboarding Section */}
          <div className="border border-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Onboarding</h3>
            <div className="space-y-3">
              <CheckboxField
                label="Meta Access erteilt"
                checked={formData.meta_access_granted}
                onChange={(checked) => setFormData({ ...formData, meta_access_granted: checked })}
              />
              {formData.service_package !== 'ad_only' && (
                <>
                </>
              )}
            </div>
          </div>

          {/* Content Production Section - Hidden for Ads Only */}
          {project.service_package !== 'ad_only' && (
            <div className="border border-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-4">Content Production</h3>
              <div className="space-y-3">
                <CheckboxField
                  label="Content Call vereinbart"
                  checked={formData.content_call_scheduled}
                  onChange={(checked) => setFormData({ ...formData, content_call_scheduled: checked })}
                />
                <CheckboxField
                  label="Content Call vorbereitet"
                  checked={formData.content_call_prepared}
                  onChange={(checked) => setFormData({ ...formData, content_call_prepared: checked })}
                />
                <CheckboxField
                  label="Content Call abgehalten"
                  checked={formData.content_call_completed}
                  onChange={(checked) => setFormData({ ...formData, content_call_completed: checked })}
                />
              </div>
            </div>
          )}

          {/* Media Budget — per-platform planned splits */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Media Budget geplant (€)</label>
            <div className="space-y-2">
              {[
                { key: 'planned_budget_meta', label: 'Meta' },
                { key: 'planned_budget_tiktok', label: 'TikTok' },
                { key: 'planned_budget_youtube', label: 'YouTube' },
                { key: 'planned_budget_follower', label: 'Follower' },
                { key: 'planned_budget_additional', label: 'Zusätzlich' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  <label className="text-xs text-gray-400 w-20 shrink-0">{label}</label>
                  <input
                    type="number"
                    value={(formData as any)[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value } as any)}
                    min="0"
                    step="0.01"
                    placeholder="0"
                    className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-gray-500 text-sm">€</span>
                </div>
              ))}
            </div>
            {/* Live total of per-platform splits */}
            {(() => {
              const total = ['planned_budget_meta', 'planned_budget_tiktok', 'planned_budget_youtube', 'planned_budget_follower', 'planned_budget_additional']
                .reduce((sum, k) => sum + (Number((formData as any)[k]) || 0), 0);
              const adBudgetNum = Number(formData.ad_budget) || 0;
              const diff = total - adBudgetNum;
              return (
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-gray-400">Summe Plattformen: <strong className={Math.abs(diff) > 1 ? 'text-yellow-400' : 'text-green-400'}>{total.toFixed(2)}€</strong></span>
                  {Math.abs(diff) > 1 && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, ad_budget: total.toFixed(2) } as any)}
                      className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                    >
                      Gesamt-Budget auf {total.toFixed(2)}€ setzen
                    </button>
                  )}
                </div>
              );
            })()}
            <div className="mt-2 pt-2 border-t border-gray-700">
              <label className="block text-xs text-gray-400 mb-1">Gesamt Media Budget (€)</label>
              <input
                type="number"
                value={formData.ad_budget}
                onChange={(e) => setFormData({ ...formData, ad_budget: e.target.value as any })}
                min="0"
                step="0.01"
                placeholder="z.B. 650"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Budget Shift Logs */}
            {budgetShiftLogs.length > 0 && (
              <div className="mt-2 space-y-1">
                {budgetShiftLogs.map((log: any) => (
                  <div key={log.id} className={`text-xs p-2 rounded-lg ${log.to_project_id === project.id ? 'bg-green-900/30 text-green-400' : 'bg-orange-900/30 text-orange-400'}`}>
                    {log.to_project_id === project.id ? (
                      <span>+ {(log.amount_cents / 100).toFixed(2)}€ erhalten von <strong>{log.fromName}</strong></span>
                    ) : (
                      <span>- {(log.amount_cents / 100).toFixed(2)}€ verschoben zu <strong>{log.toName}</strong></span>
                    )}
                    <span className="text-gray-500 ml-2">{new Date(log.created_at).toLocaleDateString('de-DE')}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Budget Shift */}
            {currentSpend !== null && (project.ad_budget / 100) > currentSpend && (
              <div className="mt-3 border border-gray-700 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-2">
                  Ungenutztes Budget: <strong className="text-white">{((project.ad_budget / 100) - currentSpend).toFixed(2)}€</strong>
                  <span className="text-gray-500 ml-1">(Spend: {currentSpend.toFixed(2)}€ / Budget: {(project.ad_budget / 100).toFixed(2)}€)</span>
                </p>
                {!shiftTarget ? (
                  <div>
                    <input
                      type="text"
                      placeholder="Projekt suchen zum Budget verschieben..."
                      value={shiftSearch}
                      onChange={(e) => {
                        setShiftSearch(e.target.value);
                        searchShiftTargets(e.target.value);
                      }}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    {shiftSearchResults.length > 0 && (
                      <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                        {shiftSearchResults.map((p: any) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => { setShiftTarget(p); setShiftSearch(''); setShiftSearchResults([]); }}
                            className="w-full text-left px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-white transition-colors"
                          >
                            <span className="font-medium">{p.artist_name} - {p.release_name}</span>
                            <span className="text-gray-400 ml-2">({(p.ad_budget / 100).toFixed(0)}€)</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-3">
                    <p className="text-sm text-orange-300 mb-2">
                      <strong>{((project.ad_budget / 100) - currentSpend).toFixed(2)}€</strong> verschieben zu:
                    </p>
                    <p className="text-sm text-white font-medium mb-1">
                      {shiftTarget.artist_name} - {shiftTarget.release_name}
                    </p>
                    <p className="text-xs text-gray-400 mb-1">
                      Budget: {(shiftTarget.ad_budget / 100).toFixed(2)}€ → {((shiftTarget.ad_budget + project.ad_budget - Math.round(currentSpend * 100)) / 100).toFixed(2)}€
                    </p>
                    <p className="text-xs text-gray-400 mb-1">
                      Dieses Projekt: Ende → <strong className="text-white">heute</strong>
                    </p>
                    <p className="text-xs text-gray-400 mb-3">
                      Zielprojekt: Ende → <strong className="text-white">{(() => { const d = new Date(); d.setDate(d.getDate() + Math.ceil(((project.ad_budget / 100) - currentSpend) / 20)); return d.toLocaleDateString('de-DE'); })()}</strong>
                      <span className="text-gray-500 ml-1">({Math.ceil(((project.ad_budget / 100) - currentSpend) / 20)} Tage, {((project.ad_budget / 100) - currentSpend).toFixed(2)}€ / 20€ pro Tag)</span>
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={executeBudgetShift}
                        disabled={shifting}
                        className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                      >
                        {shifting ? 'Verschiebe...' : 'Budget verschieben'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShiftTarget(null)}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm"
                      >
                        Abbrechen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Timeline Section */}
          <div className="border border-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Timeline</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Posting Start Datum
                </label>
                <div className="relative">
                  <DatePicker
                    selected={formData.posting_start_date ? new Date(formData.posting_start_date) : null}
                    onChange={(date: Date | null) => {
                      setFormData({ ...formData, posting_start_date: date ? date.toISOString().split('T')[0] : '' });
                    }}
                    dateFormat="dd.MM.yyyy"
                    placeholderText="dd.mm.yyyy"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Release Datum
                </label>
                <div className="relative">
                  <DatePicker
                    selected={formData.release_date ? new Date(formData.release_date) : null}
                    onChange={(date: Date | null) => {
                      if (date) {
                        const releaseDate = date.toISOString().split('T')[0];
                        // Calculate posting_start_date: 11 days before release
                        const postingStart = new Date(date);
                        postingStart.setDate(postingStart.getDate() - 11);
                        
                        // Campaign start = release date
                        const campaignStart = releaseDate;
                        
                        // Campaign end = 30 days after release
                        const campaignEndDate = new Date(date);
                        campaignEndDate.setDate(campaignEndDate.getDate() + 30);
                        const campaignEnd = campaignEndDate.toISOString().split('T')[0];
                        
                        setFormData({ 
                          ...formData, 
                          release_date: releaseDate,
                          posting_start_date: postingStart.toISOString().split('T')[0],
                          campaign_start_date: campaignStart,
                          campaign_end_date: campaignEnd
                        });
                      } else {
                        setFormData({ ...formData, release_date: '' });
                      }
                    }}
                    dateFormat="dd.MM.yyyy"
                    placeholderText="dd.mm.yyyy"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Kampagne Start
                </label>
                <div className="relative">
                  <DatePicker
                    selected={formData.campaign_start_date ? new Date(formData.campaign_start_date) : null}
                    onChange={(date: Date | null) => {
                      setFormData({ ...formData, campaign_start_date: date ? date.toISOString().split('T')[0] : '' });
                    }}
                    dateFormat="dd.MM.yyyy"
                    placeholderText="dd.mm.yyyy"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Kampagnen Ende
                </label>
                <div className="relative">
                  <DatePicker
                    selected={formData.campaign_end_date ? new Date(formData.campaign_end_date) : null}
                    onChange={(date: Date | null) => {
                      setFormData({ ...formData, campaign_end_date: date ? date.toISOString().split('T')[0] : '' });
                    }}
                    dateFormat="dd.MM.yyyy"
                    placeholderText="dd.mm.yyyy"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="bg-gray-900 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Links</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Reporting Dashboard URL
                </label>
                {linkedReports.length > 0 && (
                  <div className="mb-3 space-y-1">
                    {linkedReports.map((report) => (
                      <div key={report.id} className="text-sm text-green-400 flex items-center justify-between bg-gray-800 rounded-lg p-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="truncate">✓ <strong>{report.name}</strong></span>
                          {report.share_url && (
                            <a 
                              href={report.share_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 underline flex-shrink-0 text-xs"
                            >
                              Report →
                            </a>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => unlinkReport(report.id)}
                          className="text-red-400 hover:text-red-300 flex-shrink-0"
                          title="Report entfernen"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search reports to link..."
                    value={reportSearch}
                    onChange={(e) => {
                      setReportSearch(e.target.value);
                      searchReports(e.target.value);
                    }}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {reportSearchResults.length > 0 && (
                    <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                      {reportSearchResults.map((report: any) => (
                        <button
                          key={report.id}
                          type="button"
                          onClick={() => linkReport({ id: report.id, name: report.name, share_url: report.share_url })}
                          className="w-full text-left px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-white transition-colors"
                        >
                          <div className="font-medium">{report.name}</div>
                          <div className="text-xs text-gray-400 truncate">{report.share_url}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="url"
                  value={formData.reporting_dashboard_url}
                  onChange={(e) => setFormData({ ...formData, reporting_dashboard_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {reportRecommendations.length > 0 && (
                  <div className="mt-2 p-3 bg-gray-800 rounded-lg border border-gray-700">
                    <p className="text-xs text-gray-400 mb-2">Suggested campaigns with similar names:</p>
                    <ul className="space-y-1">
                      {reportRecommendations.map((rec, idx) => (
                        <li key={idx} className="text-xs text-gray-300">
                          {rec.campaign_name}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-gray-500 mt-2">
                      These campaigns weren't auto-matched. Check if any match your project.
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Release Sheet (Notion / Google Docs)
                </label>
                {formData.notion_page_url ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="url"
                      value={formData.notion_page_url}
                      onChange={(e) => setFormData({ ...formData, notion_page_url: e.target.value })}
                      placeholder="https://www.notion.so/ oder https://docs.google.com/..."
                      className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <a
                      href={formData.notion_page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm whitespace-nowrap"
                    >
                      Öffnen
                    </a>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="url"
                      value={formData.notion_page_url}
                      onChange={(e) => setFormData({ ...formData, notion_page_url: e.target.value })}
                      placeholder="https://www.notion.so/ oder https://docs.google.com/..."
                      className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        if (!confirm(`Notion Release Sheet für "${project.artist_name} - ${project.release_name}" erstellen?`)) return;
                        try {
                          const toastId = toast.loading('Erstelle Notion Sheet...');
                          const res = await fetch(`https://wrlgoxbzlngdtomjhvnz.supabase.co/functions/v1/create-notion-sheet`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                            },
                            body: JSON.stringify({ project_id: project.id }),
                          });
                          const data = await res.json();
                          if (data.success && data.page_url) {
                            setFormData(prev => ({ ...prev, notion_page_url: data.page_url }));
                            toast.success(`Notion Sheet erstellt: ${data.title}`, { id: toastId });
                            if (data.invite_note) {
                              toast(data.invite_note, { duration: 6000, icon: 'ℹ️' });
                            }
                          } else {
                            toast.error(data.error || 'Fehler beim Erstellen', { id: toastId });
                          }
                        } catch (err) {
                          console.error('Error creating Notion sheet:', err);
                          toast.error('Fehler beim Erstellen des Notion Sheets');
                        }
                      }}
                      className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm whitespace-nowrap"
                    >
                      + Erstellen
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  song.so URLs (eine pro Zeile)
                </label>
                <textarea
                  value={formData.song_so_urls}
                  onChange={(e) => setFormData({ ...formData, song_so_urls: e.target.value })}
                  placeholder="https://song.so/..."
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Notizen
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Interne Notizen zum Projekt..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center gap-3 pt-4 border-t border-gray-800">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowArchiveConfirm(true)}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors flex items-center gap-2"
                disabled={loading || deleting || archiving}
              >
                <Archive className="h-4 w-4" />
                Archivieren
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                disabled={loading || deleting || archiving}
              >
                <Trash2 className="h-4 w-4" />
                Löschen
              </button>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                disabled={loading || deleting}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors flex items-center gap-2"
                disabled={loading || deleting}
              >
                {loading && <Loader className="h-4 w-4 animate-spin" />}
                Speichern
              </button>
            </div>
          </div>
        </form>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-white mb-4">Projekt löschen?</h3>
              <p className="text-gray-300 mb-4">
                Möchten Sie das Projekt <span className="font-semibold text-white">{project.release_name}</span> wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
              
              {/* Related data warning */}
              <div className="bg-gray-900 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-red-400 mb-3">Folgende Daten werden ebenfalls gelöscht:</p>
                <ul className="space-y-2 text-sm text-gray-300">
                  {relatedCounts.releases > 0 && (
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      {relatedCounts.releases} Release{relatedCounts.releases > 1 ? 's' : ''}
                    </li>
                  )}
                  {relatedCounts.submissions > 0 && (
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      {relatedCounts.submissions} Video{relatedCounts.submissions > 1 ? 's' : ''}
                    </li>
                  )}
                  {relatedCounts.adCreatives > 0 && (
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      {relatedCounts.adCreatives} Ad Creative{relatedCounts.adCreatives > 1 ? 's' : ''}
                    </li>
                  )}
                  {relatedCounts.releases === 0 && relatedCounts.submissions === 0 && relatedCounts.adCreatives === 0 && (
                    <li className="text-gray-500">Keine verbundenen Daten gefunden</li>
                  )}
                </ul>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  disabled={deleting}
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  disabled={deleting}
                >
                  {deleting && <Loader className="h-4 w-4 animate-spin" />}
                  Löschen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Archive Confirmation Modal */}
        {showArchiveConfirm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-white mb-4">Projekt archivieren?</h3>
              <p className="text-gray-300 mb-4">
                Möchten Sie das Projekt <span className="font-semibold text-white">{project.release_name}</span> archivieren?
              </p>
              
              <div className="bg-gray-900 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-yellow-400 mb-3">Folgende Daten werden ebenfalls archiviert:</p>
                <ul className="space-y-2 text-sm text-gray-300">
                  {relatedCounts.releases > 0 && (
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>
                      {relatedCounts.releases} Release{relatedCounts.releases > 1 ? 's' : ''}
                    </li>
                  )}
                  {relatedCounts.submissions > 0 && (
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>
                      {relatedCounts.submissions} Video{relatedCounts.submissions > 1 ? 's' : ''}
                    </li>
                  )}
                  {relatedCounts.adCreatives > 0 && (
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>
                      {relatedCounts.adCreatives} Ad Creative{relatedCounts.adCreatives > 1 ? 's' : ''}
                    </li>
                  )}
                </ul>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowArchiveConfirm(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  disabled={archiving}
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleArchive}
                  className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  disabled={archiving}
                >
                  {archiving && <Loader className="h-4 w-4 animate-spin" />}
                  Archivieren
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CheckboxField: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-3 cursor-pointer group">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="w-4 h-4 rounded border-gray-700 bg-gray-800 text-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-offset-0"
    />
    <span className="text-gray-300 group-hover:text-white transition-colors">
      {label}
    </span>
  </label>
);
